import { Building2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FundFormData } from './types';
import type { FundOption } from '@/types/fund';

interface BasicInformationSectionProps {
  formData: FundFormData;
  errors: Record<string, string>;
  availableFunds: FundOption[];
  onInputChange: (field: keyof FundFormData, value: string) => void;
}

export function BasicInformationSection({
  formData,
  errors,
  availableFunds,
  onInputChange,
}: BasicInformationSectionProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-slate-700" />
          <CardTitle>Basic Information</CardTitle>
        </div>
        <CardDescription>Fund identification and core details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Fund Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => onInputChange('name', e.target.value)}
              placeholder="e.g., DFF Capital Partners Fund I"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              value={formData.display_name}
              onChange={(e) => onInputChange('display_name', e.target.value)}
              placeholder="Optional display name"
            />
          </div>
          <div>
            <Label htmlFor="fund_type">Fund Type *</Label>
            <Select
              value={formData.fund_type}
              onValueChange={(value) => onInputChange('fund_type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="main">Main Fund</SelectItem>
                <SelectItem value="feeder">Feeder Fund</SelectItem>
                <SelectItem value="master">Master Fund</SelectItem>
                <SelectItem value="aiv">
                  Alternative Investment Vehicle (AIV)
                </SelectItem>
                <SelectItem value="opportunity">Opportunity Fund</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="vintage_year">Vintage Year *</Label>
            <Input
              id="vintage_year"
              type="number"
              value={formData.vintage_year}
              onChange={(e) => onInputChange('vintage_year', e.target.value)}
              placeholder="2024"
              className={errors.vintage_year ? 'border-red-500' : ''}
            />
            {errors.vintage_year && (
              <p className="text-sm text-red-600 mt-1">{errors.vintage_year}</p>
            )}
          </div>
        </div>

        {/* Fund Relationships - Show for feeder, master, and opportunity funds */}
        {(formData.fund_type === 'feeder' ||
          formData.fund_type === 'master' ||
          formData.fund_type === 'opportunity') && (
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-4">
              Fund Relationships
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(formData.fund_type === 'feeder' ||
                formData.fund_type === 'opportunity') && (
                <div>
                  <Label htmlFor="parent_fund_id">Parent Fund</Label>
                  <Select
                    value={formData.parent_fund_id}
                    onValueChange={(value) =>
                      onInputChange('parent_fund_id', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent fund" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No parent fund</SelectItem>
                      {availableFunds
                        .filter(
                          (fund) =>
                            fund.fund_type === 'main' ||
                            fund.fund_type === 'master'
                        )
                        .map((fund) => (
                          <SelectItem key={fund.id} value={fund.id}>
                            {fund.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.fund_type === 'feeder' && (
                <div>
                  <Label htmlFor="master_fund_id">Master Fund</Label>
                  <Select
                    value={formData.master_fund_id}
                    onValueChange={(value) =>
                      onInputChange('master_fund_id', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select master fund" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No master fund</SelectItem>
                      {availableFunds
                        .filter((fund) => fund.fund_type === 'master')
                        .map((fund) => (
                          <SelectItem key={fund.id} value={fund.id}>
                            {fund.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
